import React, { useEffect, useState, useCallback } from 'react';
import { IonChip, IonLabel } from '@ionic/react';
import { isApiOverridden, getApiBaseUrl, setApiBaseUrlOverride, clearApiCache, getApiEnvironment, toggleApiEnvironment } from '../api';

const chipStyle: React.CSSProperties = {
  '--background': '#ffc409',
  '--color': '#000',
  fontWeight: 600
} as any;

const EnvironmentBadge: React.FC = () => {
  const [show, setShow] = useState(false);
  const [text, setText] = useState('');
  const [env, setEnv] = useState<'dev' | 'prod'>('dev');
  const [base, setBase] = useState<string>('');

  const evaluate = useCallback(() => {
    const currentEnv = getApiEnvironment();
    setEnv(currentEnv);
    const current = getApiBaseUrl();
    setBase(current);
    // Show chip for developers always (env detection done via env key; if non-dev env will be 'prod' but we might still want to hide). Simpler: hide if not developer by checking absence of segment logic: if env is 'prod' AND no override and base is PROD_URL then maybe hide.
    // We can't directly import PROD_URL; rely on heuristic: if env==='prod' and !isApiOverridden() then hide.
    if (currentEnv === 'prod' && !isApiOverridden()) {
      setShow(false);
      setText('');
    } else {
      setShow(true);
      setText(currentEnv.toUpperCase());
    }
  }, []);

  useEffect(() => {
    evaluate();
    const storageListener = (e: StorageEvent) => {
      if (e.key === 'apiBaseUrlOverride' || e.key === 'me') {
        evaluate();
      }
    };
    window.addEventListener('storage', storageListener);
    // Custom event for same-tab updates
    const customListener = () => evaluate();
    window.addEventListener('apiBaseUrlChanged', customListener as EventListener);
    return () => {
      window.removeEventListener('storage', storageListener);
      window.removeEventListener('apiBaseUrlChanged', customListener as EventListener);
    };
  }, [evaluate]);

  const handleClick = (e: React.MouseEvent) => {
    if (!show) return;
    const isShift = e.shiftKey;
    if (env === 'dev') {
      if (isShift && isApiOverridden()) {
        const current = getApiBaseUrl();
        const confirmReset = window.confirm(`Återställ API-bas-URL från '${current}' till standard?`);
        if (confirmReset) {
          setApiBaseUrlOverride('');
          clearApiCache();
          evaluate();
          window.dispatchEvent(new Event('apiBaseUrlChanged'));
        }
        return;
      }
      // Toggle to prod if simple click without shift
      if (!isShift) {
        toggleApiEnvironment();
        evaluate();
      }
    } else {
      // Currently prod: clicking toggles back to dev
      toggleApiEnvironment();
      evaluate();
    }
  };

  if (!show) return null;
  const shiftHint = env === 'dev' && isApiOverridden() ? 'Shift-klick för reset override' : '';
  const toggleHint = 'Klicka för att växla DEV/PROD';
  return (
    <IonChip color="warning" style={chipStyle} onClick={handleClick} title={`Miljö: ${env.toUpperCase()}\nBas: ${base}\n${toggleHint}${shiftHint ? '\n' + shiftHint : ''}`} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{text}</IonLabel>
    </IonChip>
  );
};

export default EnvironmentBadge;
